import React, { useEffect } from 'react';

/**
 * Reusable premium Modal component with standard Axiomus styling.
 * Supports HSL tailored dark-mode, glassmorphism backdrops, interactive animations,
 * and optional form action controls (Save, Cancel, Delete).
 * 
 * Props:
 * - isOpen (boolean): visibility toggle
 * - onClose (function): triggered when closing the modal
 * - title (string | node): title text shown in the header
 * - subtitle (string | node): optional small text below the title
 * - children (node): body content
 * - size (string): 'sm' | 'md' | 'lg' | 'xl' | 'full'
 * - preventCloseOnOverlay (boolean): if true, clicking the backdrop won't trigger onClose
 * - hideCloseButton (boolean): if true, hides the top right 'X' close button
 * 
 * Form specific props (optional):
 * - onSave (function): if passed, renders a standardized primary save button in the footer
 * - onDelete (function): if passed, renders a standardized danger delete button in the footer
 * - busy (boolean): disables action buttons and shows a loading state
 * - saveLabel (string): custom label for the save button
 * - cancelLabel (string): custom label for the cancel button
 * - deleteLabel (string): custom label for the delete button
 */
export default function Modal({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    size = 'md',
    preventCloseOnOverlay = false,
    hideCloseButton = false,
    // Form action props:
    onSave,
    onDelete,
    busy = false,
    saveLabel = 'Enregistrer',
    cancelLabel = 'Annuler',
    deleteLabel = 'Supprimer'
}) {
    // Prevent background scrolling when modal is active
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
        return () => {
            document.body.classList.remove('overflow-hidden');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-[95vw] h-[90vh]'
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !preventCloseOnOverlay && onClose) {
            onClose();
        }
    };

    return (
        <div 
            onClick={handleBackdropClick}
            className="fixed inset-0 z-[9999] overflow-y-auto bg-slate-900/60 backdrop-blur-md p-4 flex items-center justify-center animate-fade-in"
        >
            <div 
                className={`relative w-full ${sizeClasses[size] || sizeClasses.md} bg-white dark:bg-gray-950 rounded-3xl border border-gray-100 dark:border-white/[0.08] shadow-[0_20px_50px_rgba(15,23,42,0.15)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col justify-between max-h-[90vh] transition-all duration-300 transform scale-100 animate-scale-up`}
            >
                {/* Header */}
                {(title || !hideCloseButton) && (
                    <div className="px-8 py-5 border-b border-gray-100 dark:border-white/[0.04] flex items-center justify-between bg-gradient-to-r from-blue-500/5 to-cyan-500/5">
                        <div className="flex items-center gap-2 text-left">
                            <div>
                                {title && <h3 className="text-base font-bold text-gray-800 dark:text-white leading-tight">{title}</h3>}
                                {subtitle && <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5">{subtitle}</p>}
                            </div>
                        </div>
                        {!hideCloseButton && onClose && (
                            <button 
                                onClick={onClose}
                                aria-label="Fermer la modal"
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center transition-all cursor-pointer"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                {/* Body Content */}
                <div className="p-8 overflow-y-auto flex-1 text-left">
                    {children}
                </div>

                {/* Optional Action Footer */}
                {onSave && (
                    <div className="px-8 py-4 border-t border-gray-100 dark:border-white/[0.04] bg-gray-50/50 dark:bg-slate-950/40 flex items-center justify-between gap-3">
                        <div>
                            {onDelete && (
                                <button
                                    onClick={onDelete}
                                    disabled={busy}
                                    className="px-4 py-2 text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/30 rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deleteLabel}
                                </button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {onClose && (
                                <button
                                    onClick={onClose}
                                    disabled={busy}
                                    className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {cancelLabel}
                                </button>
                            )}
                            <button
                                onClick={onSave}
                                disabled={busy}
                                className="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? `${saveLabel}…` : saveLabel}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
